import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';
import TacoFaq from '../faqs/TacoFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function TacoCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'taco' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`Taco ${vehicle_type} Catering in ${city_name}`}
        type='website'
        canonical='/food-truck-catering/taco-catering'
        description={`Book taco ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties. Get free quotes from top taco ${vehicleType}s with customizable menus.`}
        image='https://static.seattlefoodtruck.com/photos/Taco_Header.webp'
        structuredData={{
          cateringService: {
            name: `Taco ${vehicle_type} Catering in ${city_name}`,
            description: `Book taco ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties.`,
            url: '/food-truck-catering/taco-catering',
            serviceType: 'Taco Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Taco Catering', item: '/food-truck-catering/taco-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/Taco_Header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Taco {vehicle_type} Catering for Your Next Event</h1>
            <p>Nothing brings people together like tacos! Whether you&#39;re planning a <Link to='/food-truck-catering/corporate-catering'>corporate gathering</Link>, <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, a taco food {vehicleType} is a guaranteed crowd-pleaser. From classic street-style tacos to gourmet fusion creations, {site_title} makes it effortless to find and book the best taco {vehicleType}s in town. Simply tell us about your event, receive tailored quotes, and secure your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Taco Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='taco'
        image='https://static.seattlefoodtruck.com/photos/taco1.webp'
        optionsText='Choose from authentic street tacos, Baja fish tacos, fusion tacos, and more.'
      />

      <FeaturedTrucks title={`Featured Taco Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Taco Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What Makes Us Different?</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/taco2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Authentic & Gourmet Taco Options</h4>
            <p>
              {site_title} partners with the best taco {vehicleType}s in the city, offering a variety of styles and flavors. Whether you&#39;re looking for traditional al pastor, crispy fish tacos, or innovative fusion creations, our food {vehicleType}s serve up fresh, high-quality ingredients with bold flavors. Many of our vendors make their tortillas, salsas, and marinades from scratch, ensuring an unforgettable taco experience.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/taco4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Effortless Taco Catering for Any Event</h4>
            <p>
              Booking a taco {vehicleType} should be as easy as enjoying one. Our streamlined process allows you to enter your event details, receive multiple quotes from verified taco vendors, and book in minutes. Whether you&#39;re planning a casual backyard party or a large corporate event, our platform ensures a hassle-free experience.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/taco3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Customizable Menus & Dietary Options</h4>
            <p>
              Want a taco bar with all the fixings? Need vegetarian, vegan, or gluten-free taco options? Our taco {vehicleType}s are flexible and can customize menus to fit your guests&#39; preferences. From build-your-own taco stations to chef-curated taco tastings, we ensure your catering is as unique as your event.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks
        text1='Tell us about your event, location, and taco preferences.'
        text2={`Get customized offers from available taco food ${vehicleType}s that fit your budget.`}
      />

      <AnyEvent title={`Taco Food ${vehicle_type} Catering for Any Event`} />

      <Testimonials title="Join Thousands Who've Booked Taco Catering With Us!" />

      <CostEstimator title='Taco Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <TacoFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default TacoCatering;
